import { useState, useEffect, lazy, Suspense } from 'react';
import { BrowserRouter, useLocation } from "react-router-dom";
import { AppRoutes } from "./router";
import { I18nextProvider } from "react-i18next";
import i18n from "./i18n";
import { Header } from './components/feature/Header';
import { Footer } from './components/feature/Footer';
import RouteRelatedContent from './components/feature/RouteRelatedContent';
import { SplashScreen } from './components/feature/SplashScreen';
import { isHeadlessLanding } from './lib/landingRoutes';
import { RateLimitModal } from './components/feature/RateLimitModal';
import { StickyHireBar } from './components/feature/StickyHireBar';
import { BackToTop } from './components/feature/BackToTop';
import { HireExpertsProvider } from './context/HireExpertsContext';
import { useSEO } from './hooks/useSEO';
import { usePageTracker } from './hooks/usePageTracker';
import { useGoogleIntegrations } from './hooks/useGoogleIntegrations';
import { useGrowthIntelTracking } from './hooks/useGrowthIntel';
import ConsentBanner from './components/feature/ConsentBanner';
import { ArticlesProvider } from './context/ArticlesContext';
import { CaseStudiesProvider } from './context/CaseStudiesContext';
import { CMSContentProvider } from './hooks/useCMSContent';
import { AcademyAccessProvider } from './hooks/useAcademyAccess';

// The chat widget is lazy-loaded after first interaction / idle so it never
// slows the initial page load.
const ChatWidget = lazy(() => import('./components/feature/ChatWidget'));

/** Mounts the chat widget only once the browser is idle or the user interacts. */
function useIdleMount(): boolean {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    if (ready) return;
    let done = false;
    const activate = () => { if (!done) { done = true; setReady(true); } };

    const hasIdle = typeof window !== 'undefined' && 'requestIdleCallback' in window;
    const idleId = hasIdle
      ? (window as unknown as { requestIdleCallback: (cb: () => void, opts?: { timeout: number }) => number })
          .requestIdleCallback(activate, { timeout: 3500 })
      : window.setTimeout(activate, 2500);

    window.addEventListener('pointerdown', activate, { once: true, passive: true });
    window.addEventListener('keydown', activate, { once: true });
    window.addEventListener('scroll', activate, { once: true, passive: true });

    return () => {
      window.removeEventListener('pointerdown', activate);
      window.removeEventListener('keydown', activate);
      window.removeEventListener('scroll', activate);
      if (hasIdle) {
        (window as unknown as { cancelIdleCallback?: (id: number) => void }).cancelIdleCallback?.(idleId);
      } else {
        clearTimeout(idleId);
      }
    };
  }, [ready]);
  return ready;
}

/**
 * Site-wide safety net: any <img> that fails to load (e.g. a Readdy image
 * service hiccup) is swapped to a branded navy/teal gradient instead of the
 * browser's broken-image icon. Complements the shared <SafeImage> component.
 */
function useImageFallbackGuard() {
  useEffect(() => {
    const TRANSPARENT_PX =
      'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';
    const handler = (event: Event) => {
      const el = event.target as HTMLElement | null;
      if (!el || el.tagName !== 'IMG') return;
      const img = el as HTMLImageElement;
      if (img.dataset.safeFallback === '1') return;
      img.dataset.safeFallback = '1';
      img.style.backgroundImage = 'linear-gradient(135deg, #0b1220 0%, #16233b 45%, #0f766e 100%)';
      img.style.backgroundSize = 'cover';
      img.style.backgroundPosition = 'center';
      img.src = TRANSPARENT_PX;
    };
    window.addEventListener('error', handler, true);
    return () => window.removeEventListener('error', handler, true);
  }, []);
}

function AppShell() {
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith('/admin');
  // Headerless SEO landing pages (/managed-it/{slug}) render their own minimal chrome.
  const isLandingRoute = isHeadlessLanding(location.pathname);
  // Creators Cut is an internal tool: no site header, footer, chat or sticky bars.
  const isCreatorsCut = location.pathname === '/creators-cut' || location.pathname.startsWith('/creators-cut/');
  // Cendien Originals is a standalone viewing experience with its own nav and footer.
  const isOriginals = location.pathname === '/originals' || location.pathname.startsWith('/originals/');
  const hideChrome = isAdminRoute || isLandingRoute || isCreatorsCut || isOriginals;
  const chatReady = useIdleMount();

  useSEO();
  usePageTracker();
  useGoogleIntegrations();
  useGrowthIntelTracking();
  useImageFallbackGuard();

  return (
    <HireExpertsProvider>
      <div className="min-h-screen bg-white flex flex-col">
        {!hideChrome && <Header />}
        <main className="flex-1">
          <AppRoutes />
          {!hideChrome && <RouteRelatedContent />}
        </main>
        {!hideChrome && <Footer />}
        {!isAdminRoute && !isCreatorsCut && !isOriginals && chatReady && (
          <Suspense fallback={null}>
            <ChatWidget />
          </Suspense>
        )}
        {!isAdminRoute && !isCreatorsCut && !isOriginals && <BackToTop />}
        {!hideChrome && <StickyHireBar />}
        <RateLimitModal />
        <ConsentBanner />
      </div>
    </HireExpertsProvider>
  );
}

function App() {
  // The intro splash is client-only and runs at most once per browser session.
  // It reads sessionStorage lazily (no side effects in the initializer) and
  // skips entirely when storage is unavailable, when the visitor prefers
  // reduced motion, or during SSR/prerender (no window).
  const [showSplash, setShowSplash] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    try {
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return false;
      // Headless SEO/lead landing pages load straight into the offer.
      if (isHeadlessLanding(window.location.pathname)) return false;
      if (window.location.pathname.startsWith('/creators-cut') || window.location.pathname.startsWith('/originals')) return false;
      return !sessionStorage.getItem('cendien_splash_shown');
    } catch {
      // Storage (or matchMedia) unavailable → skip the splash rather than risk it.
      return false;
    }
  });

  // Record that the splash has been shown for this session as soon as we commit
  // to showing it, so later page loads and client-side navigations never replay it.
  useEffect(() => {
    if (!showSplash) return;
    try {
      sessionStorage.setItem('cendien_splash_shown', '1');
    } catch {
      /* ignore — storage unavailable */
    }
  }, [showSplash]);

  const handleSplashComplete = () => {
    setShowSplash(false);
  };

  // Lock page scroll only while the splash is on screen, and always restore it
  // afterwards so the page content behind the overlay scrolls normally.
  useEffect(() => {
    if (showSplash) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [showSplash]);

  return (
    <I18nextProvider i18n={i18n}>
      <ArticlesProvider>
        <CaseStudiesProvider>
          <CMSContentProvider>
            <AcademyAccessProvider>
            {showSplash && <SplashScreen onComplete={handleSplashComplete} />}
            <BrowserRouter basename={__BASE_PATH__}>
              <div
                style={{
                  opacity: showSplash ? 0 : 1,
                  transition: 'opacity 0.5s ease',
                }}
              >
                <AppShell />
              </div>
            </BrowserRouter>
            </AcademyAccessProvider>
          </CMSContentProvider>
        </CaseStudiesProvider>
      </ArticlesProvider>
    </I18nextProvider>
  );
}

export default App;
